import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppState } from '@/store';
import { DateFormats } from '@/constants/dates';
import { RangePicker } from '@/components';
import { Button, Select } from '@/ui';
import { formatDateForApiRequest } from '@/utils';
import { changeHistoryQuery, commissionSelector, getCommissionHistoryList, resetHistoryQuery } from '../../feature';
import { CommissionHistoryFiltersFields } from '../../helpers';
import { StyledHistoryFilterControl, StyledHistoryFiltersRow } from '../CommissionList/CommissionListStyles';

const CommissionHistoryFilters = () => {
  const { t } = useTranslation(['commission']);
  const dispatch = useAppDispatch();
  const { historyQuery, historyFilters } = useAppState(commissionSelector);

  const categoryOptions = useMemo(
    () => (historyFilters?.categories ?? []).map((category) => ({ label: category.name, value: category.id })),
    [historyFilters?.categories],
  );

  const adminOptions = useMemo(
    () => (historyFilters?.admins ?? []).map((admin) => ({ label: admin.full_name, value: admin.id })),
    [historyFilters?.admins],
  );

  const fetchHistory = (query: typeof historyQuery) => {
    dispatch(changeHistoryQuery(query));
    dispatch(getCommissionHistoryList(query));
  };

  const handleDateRangeChange = (_: unknown, dateStrings: [string, string]) => {
    fetchHistory({
      ...historyQuery,
      [CommissionHistoryFiltersFields.DATE_FROM]:
        formatDateForApiRequest(dateStrings[0], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      [CommissionHistoryFiltersFields.DATE_TO]:
        formatDateForApiRequest(dateStrings[1], DateFormats.DATE_WITHOUT_HOURS) || undefined,
      page: 1,
    });
  };

  const handleCategoryChange = (value: unknown) => {
    fetchHistory({
      ...historyQuery,
      [CommissionHistoryFiltersFields.CATEGORY_ID]: (value as string) || undefined,
      page: 1,
    });
  };

  const handleAdminChange = (value: unknown) => {
    fetchHistory({
      ...historyQuery,
      [CommissionHistoryFiltersFields.CHANGED_BY_ADMIN_ID]: (value as string) || undefined,
      page: 1,
    });
  };

  const handleReset = () => {
    dispatch(resetHistoryQuery());
    dispatch(getCommissionHistoryList({ page: 1 }));
  };

  return (
    <StyledHistoryFiltersRow>
      <StyledHistoryFilterControl>
        <RangePicker
          placeholder={[t('filter_date_from'), t('filter_date_to')]}
          onChange={handleDateRangeChange}
          size="middle"
          format={DateFormats.DATE_WITHOUT_HOURS}
        />
      </StyledHistoryFilterControl>
      <StyledHistoryFilterControl>
        <Select
          options={categoryOptions}
          placeholder={t('filter_category')}
          value={historyQuery.category_id}
          onChange={handleCategoryChange}
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
        />
      </StyledHistoryFilterControl>
      <StyledHistoryFilterControl>
        <Select
          options={adminOptions}
          placeholder={t('filter_changed_by')}
          value={historyQuery.changed_by_admin_id}
          onChange={handleAdminChange}
          size="large"
          allowClear
          showSearch
          optionFilterProp="label"
        />
      </StyledHistoryFilterControl>
      <Button onClick={handleReset}>{t('reset_filters')}</Button>
    </StyledHistoryFiltersRow>
  );
};

export default CommissionHistoryFilters;
